/* sc-saves.js
   Lightweight save/favourites system for Sophie Conran.
   Works independently of Shopify customer accounts.

   Storage: localStorage for instant access + Cloudflare Worker API for
   cross-device sync. When a customer signs in with their email on a
   new device, their saves load from the server.

   Usage:
     SC.saves.requireSignIn(callback) — ensure signed in, then call back
     SC.saves.saveBuild(config)       — save a sofa configuration
     SC.saves.getBuilds()             — get all saved builds
     SC.saves.deleteBuild(id)         — remove a saved build
     SC.saves.toggleFavourite(id)     — toggle a gallery favourite
     SC.saves.getFavourites()         — get all favourited image IDs
     SC.saves.isSignedIn()            — check if user is signed in
     SC.saves.getUser()               — get { email, firstName }
     SC.saves.buildShareUrl(config)   — generate a shareable URL
     SC.saves.readShareUrl()          — read config from current URL
     SC.saves.renderSavedBuilds()     — render saved builds HTML
*/

(function() {
  'use strict';

  window.SC = window.SC || {};

  var STORAGE_KEY_USER = 'sc_user';
  var STORAGE_KEY_BUILDS = 'sc_builds';
  var STORAGE_KEY_FAVS = 'sc_favs';

  /* Worker API base URL — update after deploying the Worker */
  var API_BASE = 'https://cdn.sophieconran.com';

  /* Where a saved build goes to become something. The saves overlay is a
     dead end otherwise: it shows a customer what they configured and then
     offers a PDF of it, which is a record rather than a next step.

     Overridable the way API_BASE is, because the page could move and this
     file is not Liquid and cannot read a theme setting. window.SC_STUDIO_URL
     wins if a template has set one. */
  var STUDIO_URL = (typeof window !== 'undefined' && window.SC_STUDIO_URL) || '/pages/my-sofa';

  /* ── Helpers ── */
  function store(key, data) {
    try { localStorage.setItem(key, JSON.stringify(data)); } catch(e) {}
  }
  function load(key) {
    try { return JSON.parse(localStorage.getItem(key)); } catch(e) { return null; }
  }

  function userKey(base) {
    var user = load(STORAGE_KEY_USER);
    if (!user || !user.email) return base + '_guest';
    return base + '_' + user.email.toLowerCase().trim();
  }

  function generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
  }

  /* ── Server sync ── */
  function syncToServer(email, builds, favourites) {
    if (!email) return;
    try {
      fetch(API_BASE + '/api/saves', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          firstName: (load(STORAGE_KEY_USER) || {}).firstName || '',
          builds: builds
        })
      }).catch(function() { /* silent fail — local storage is the fallback */ });

      if (favourites !== undefined) {
        fetch(API_BASE + '/api/saves/favourites', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email, favourites: favourites })
        }).catch(function() {});
      }
    } catch(e) {}
  }

  /* Reading back stored saves needs verification; creating them does
     not. On a first visit with a new email the Worker has nothing to
     return, so it returns an empty account and this resolves straight
     through with no prompt at all.

     On a return visit, or on a second device, the Worker answers
     requiresVerification and hands back nothing. We then ask for a
     code and retry once. Declining leaves the customer with whatever
     is on this device, which is the same position they were in before
     they signed in, rather than an error.

     credentials: 'include' is required for the sc_id cookie to travel
     to cdn.sophieconran.com, and is matched by the per-origin CORS on
     that route. It must not be copied onto endpoints still answering
     Allow-Origin '*': browsers reject that combination outright. */
  function fetchFromServer(email, callback, retried) {
    if (!email) { callback(null); return; }
    fetch(API_BASE + '/api/saves?email=' + encodeURIComponent(email), {
      credentials: 'include'
    })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data && data.requiresVerification && !retried) {
          if (window.SC && SC.identity && SC.identity.ensureVerified) {
            SC.identity.ensureVerified({
              reason: 'Welcome back. We keep your saved sofas to your email, so we will send a quick code to make sure it is you.'
            }).then(function () {
              fetchFromServer(email, callback, true);
            }).catch(function () {
              callback(null);   /* dismissed: carry on with local data */
            });
            return;
          }
          callback(null);       /* identity layer absent: no regression */
          return;
        }
        callback(data);
      })
      .catch(function() { callback(null); });
  }

  /* Merge remote builds into local, deduplicating by id */
  function mergeBuilds(local, remote) {
    if (!remote || !remote.length) return local;
    var ids = {};
    var merged = [];
    /* Local takes priority for shared IDs */
    local.forEach(function(b) { ids[b.id] = true; merged.push(b); });
    remote.forEach(function(b) { if (!ids[b.id]) merged.push(b); });
    /* Sort by savedAt descending */
    merged.sort(function(a, b) { return (b.savedAt || '').localeCompare(a.savedAt || ''); });
    return merged.slice(0, 20);
  }

  function mergeFavourites(local, remote) {
    /* Both local and remote may be either bare-ID arrays (legacy) or
       arrays of full pin objects (current). Normalise to objects keyed
       by id; local wins on conflict so the active device is authoritative. */
    function toMap(arr) {
      var m = {};
      (arr || []).forEach(function(f) {
        if (f == null) return;
        if (typeof f === 'string') { m[f] = m[f] || { id: f }; }
        else if (f.id) { m[f.id] = f; }
      });
      return m;
    }
    var remoteMap = toMap(remote);
    var localMap = toMap(local);
    var merged = remoteMap;
    Object.keys(localMap).forEach(function(id) { merged[id] = localMap[id]; });
    return Object.keys(merged).map(function(id) { return merged[id]; });
  }

  /* ── Sign-in modal ── */
  /* Adopt guest saves into an email identity, merge with the server,
     and push the result back. Shared by the sign-in modal and by
     sc-identity.js after code verification, so the two entry points
     cannot drift. The user record is written first so userKey() below
     resolves to the target email rather than _guest. */
  function adoptAndSync(email, firstName, done) {
    store(STORAGE_KEY_USER, { email: email, firstName: firstName || '', signedIn: true });

    var guestBuilds = load(STORAGE_KEY_BUILDS + '_guest') || [];
    var localBuilds = load(userKey(STORAGE_KEY_BUILDS)) || [];

    /* Favourites: collect full objects. Prefer the gallery's live store
       (which holds the pin payloads the customer just favourited on this
       device) so they survive the sign-in and get pushed to the server. */
    var localFavs;
    if (SC.saves._collectFavourites) {
      localFavs = SC.saves._collectFavourites();
    } else {
      localFavs = SC.saves.getFavouriteObjects();
    }

    if (guestBuilds.length) {
      localBuilds = mergeBuilds(localBuilds, guestBuilds);
      try { localStorage.removeItem(STORAGE_KEY_BUILDS + '_guest'); } catch(e) {}
    }

    fetchFromServer(email, function(serverData) {
      if (serverData) {
        localBuilds = mergeBuilds(localBuilds, serverData.builds || []);
        localFavs = mergeFavourites(localFavs, serverData.favourites || []);
      }

      store(userKey(STORAGE_KEY_BUILDS), localBuilds);
      store(userKey(STORAGE_KEY_FAVS + '_obj'), localFavs);
      store(userKey(STORAGE_KEY_FAVS), localFavs.map(function(f){ return f && f.id; }).filter(Boolean));

      syncToServer(email, localBuilds, localFavs);

      /* Let the gallery rehydrate its store + custom-pin registry so the
         merged favourites render immediately, including on a fresh device. */
      if (SC.saves._applyFavourites) SC.saves._applyFavourites(localFavs);

      if (done) done();
    });
  }

  function renderSignInModal(onComplete) {
    var existing = document.getElementById('sc-signin-overlay');
    if (existing) existing.remove();

    var overlay = document.createElement('div');
    overlay.id = 'sc-signin-overlay';
    overlay.className = 'sc-signin-overlay';
    overlay.innerHTML =
      '<div class="sc-signin-backdrop"></div>' +
      '<div class="sc-signin-modal">' +
        '<button type="button" class="sc-signin-close" aria-label="Close">&times;</button>' +
        '<div class="sc-signin-content">' +
          '<div class="sc-eyebrow">Save to your account</div>' +
          '<h2 class="sc-signin-heading">Quick sign-in.</h2>' +
          '<p class="sc-signin-desc">Enter your email and we will keep your saved builds and favourites. Access them on any device by signing in with the same email.</p>' +
          '<form class="sc-signin-form" data-sc-signin-form>' +
            '<label class="sc-signin-label">' +
              '<span>Full name</span>' +
              '<input type="text" name="fullName" class="sc-signin-input" required autocomplete="name" />' +
            '</label>' +
            '<label class="sc-signin-label">' +
              '<span>Email</span>' +
              '<input type="email" name="email" class="sc-signin-input" required autocomplete="email" />' +
            '</label>' +
            '<button type="submit" class="sc-btn sc-btn-primary sc-signin-submit">Sign in & save</button>' +
          '</form>' +
          '<button type="button" class="sc-signin-skip" data-sc-signin-skip>Just save on this device</button>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);
    requestAnimationFrame(function() { overlay.classList.add('is-open'); });

    var form = overlay.querySelector('[data-sc-signin-form]');
    var closeBtn = overlay.querySelector('.sc-signin-close');
    var backdrop = overlay.querySelector('.sc-signin-backdrop');
    var skipBtn = overlay.querySelector('[data-sc-signin-skip]');

    function close() {
      overlay.classList.remove('is-open');
      setTimeout(function() { overlay.remove(); }, 300);
    }

    closeBtn.addEventListener('click', close);
    backdrop.addEventListener('click', close);

    skipBtn.addEventListener('click', function() {
      store(STORAGE_KEY_USER, { email: '', firstName: 'Guest', signedIn: true });
      close();
      if (onComplete) onComplete();
    });

    form.addEventListener('submit', function(e) {
      e.preventDefault();
      var fullName = form.querySelector('[name="fullName"]').value.trim();
      var email = form.querySelector('[name="email"]').value.trim();
      if (!fullName || !email) return;

      /* Show loading state */
      var submitBtn = form.querySelector('.sc-signin-submit');
      var originalText = submitBtn.textContent;
      submitBtn.textContent = 'Loading your saves...';
      submitBtn.disabled = true;

      /* Store the full name; keep a derived firstName so existing greetings
         ("Saved builds, <first name>.") still read naturally. */
      var firstName = fullName.split(' ')[0];
      store(STORAGE_KEY_USER, { email: email, name: fullName, firstName: firstName, signedIn: true });

      /* Capture the sign-in as a CRM lead so the team can see who has
         created an account, even if they never request a catalogue or PDF.
         Non-blocking: a backend hiccup must never block sign-in. */
      var signInPayload = {
        source: 'sc-sign-in',
        timestamp: new Date().toISOString(),
        lead: { name: fullName, email: email }
      };
      /* lead payload intentionally not logged in production (PII) */
      try {
        fetch(API_BASE + '/api/leads/sign-in', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(signInPayload)
        }).catch(function(){});
      } catch (e) {}

      /* Migrate any guest saves to this email, merge with the server,
         and push back. Extracted to adoptAndSync so the identity layer
         (sc-identity.js) runs exactly the same migration after a code
         is verified. One implementation, two doors in. */
      adoptAndSync(email, firstName, function() {
        close();
        if (onComplete) onComplete();
      });
    });

    setTimeout(function() {
      var firstInput = form.querySelector('input');
      if (firstInput) firstInput.focus();
    }, 100);
  }

  /* ── Public API ── */
  SC.saves = {

    /* Configuration */
    setApiBase: function(url) { API_BASE = url; },
    setStudioUrl: function(url) { STUDIO_URL = url; },

    isSignedIn: function() {
      var user = load(STORAGE_KEY_USER);
      return !!(user && user.signedIn);
    },

    getUser: function() {
      return load(STORAGE_KEY_USER) || { email: '', firstName: '' };
    },

    signIn: function(email, firstName) {
      store(STORAGE_KEY_USER, { email: email, firstName: firstName, signedIn: true });
    },

    signOut: function() {
      store(STORAGE_KEY_USER, { email: '', firstName: '', signedIn: false });
    },

    promptSignIn: function(onComplete) {
      renderSignInModal(onComplete);
    },

    /* Used by sc-identity.js after a verification code is accepted.
       Runs the same guest migration and server merge as the sign-in
       modal. Safe to call with the current email: it simply re-syncs. */
    adoptAndSync: function(email, firstName, done) {
      adoptAndSync(email, firstName, done || function(){});
    },

    requireSignIn: function(callback) {
      if (SC.saves.isSignedIn()) {
        callback();
      } else {
        renderSignInModal(callback);
      }
    },

    /* ── Builds ── */
    saveBuild: function(config) {
      var builds = load(userKey(STORAGE_KEY_BUILDS)) || [];
      var build = {
        id: generateId(),
        savedAt: new Date().toISOString(),
        range: config.range || '',
        rangeName: config.rangeName || '',
        size: config.size || '',
        fabric: config.fabric || '',
        fabricName: config.fabricName || '',
        cushion: config.cushion || '',
        cover: config.cover || '',
        piping: config.piping || '',
        cushionFill: config.cushionFill || '',
        legStyle: config.legStyle || '',
        legFinish: config.legFinish || '',
        frame: config.frame || '',
        price: config.price || 0,
        sku: config.sku || '',
        imageUrl: config.imageUrl || '',
        /* The PDP this build belongs to. buildShareUrl uses it so opening a
           saved build from the gallery (or anywhere else) lands on the right
           sofa page, not the page the card happened to be viewed on. */
        pageUrl: config.pageUrl || ''
      };
      builds.unshift(build);
      if (builds.length > 20) builds = builds.slice(0, 20);
      store(userKey(STORAGE_KEY_BUILDS), builds);

      /* Sync to server */
      var user = load(STORAGE_KEY_USER);
      if (user && user.email) {
        syncToServer(user.email, builds);
      }

      return build;
    },

    getBuilds: function() {
      return load(userKey(STORAGE_KEY_BUILDS)) || [];
    },

    deleteBuild: function(id) {
      var builds = load(userKey(STORAGE_KEY_BUILDS)) || [];
      builds = builds.filter(function(b) { return b.id !== id; });
      store(userKey(STORAGE_KEY_BUILDS), builds);

      /* Sync to server */
      var user = load(STORAGE_KEY_USER);
      if (user && user.email) {
        syncToServer(user.email, builds);
      }
    },

    /* ── Favourites ── */
    toggleFavourite: function(imageId) {
      var favs = load(userKey(STORAGE_KEY_FAVS)) || [];
      var idx = favs.indexOf(imageId);
      if (idx === -1) {
        favs.push(imageId);
      } else {
        favs.splice(idx, 1);
      }
      store(userKey(STORAGE_KEY_FAVS), favs);

      /* Sync to server */
      var user = load(STORAGE_KEY_USER);
      if (user && user.email) {
        syncToServer(user.email, undefined, favs);
      }

      return idx === -1;
    },

    isFavourite: function(imageId) {
      var favs = load(userKey(STORAGE_KEY_FAVS)) || [];
      return favs.indexOf(imageId) !== -1;
    },

    /* Pull this user's data from the Worker and apply it locally. Useful on
       page load for an already-signed-in session on a fresh device, where
       nothing has triggered the sign-in merge. */
    refreshFromServer: function(done) {
      var user = load(STORAGE_KEY_USER);
      if (!user || !user.email) { if (done) done(false); return; }
      fetchFromServer(user.email, function(serverData) {
        if (!serverData) { if (done) done(false); return; }
        if (serverData.builds) store(userKey(STORAGE_KEY_BUILDS), serverData.builds);
        var favObjs = mergeFavourites(SC.saves.getFavouriteObjects(), serverData.favourites || []);
        store(userKey(STORAGE_KEY_FAVS + '_obj'), favObjs);
        store(userKey(STORAGE_KEY_FAVS), favObjs.map(function(f){ return f && f.id; }).filter(Boolean));
        if (SC.saves._applyFavourites) SC.saves._applyFavourites(favObjs);
        if (done) done(true);
      });
    },

    getFavourites: function() {
      return load(userKey(STORAGE_KEY_FAVS)) || [];
    },

    /* ── Full-object favourites ──
       The gallery favourites overlay needs each saved item's full payload
       (image, fabric, price) to render a card. A bare ID is useless on a
       fresh device because the pin payload only exists in the originating
       device's memory/localStorage. So we persist and sync the full
       objects under their own key, keyed (like everything else) per user. */
    getFavouriteObjects: function() {
      return load(userKey(STORAGE_KEY_FAVS + '_obj')) || [];
    },

    /* Replace the whole favourites set with an array of full pin objects.
       Each object must have a stable `id`. Stores locally and pushes to
       the Worker so other devices pick it up on next sign-in. */
    setFavouriteObjects: function(objects) {
      objects = Array.isArray(objects) ? objects : [];
      store(userKey(STORAGE_KEY_FAVS + '_obj'), objects);
      /* Keep the bare-ID list in step for any code that reads it */
      var ids = objects.map(function(o) { return o && o.id; }).filter(Boolean);
      store(userKey(STORAGE_KEY_FAVS), ids);
      var user = load(STORAGE_KEY_USER);
      if (user && user.email) {
        syncToServer(user.email, undefined, objects);
      }
    },

    /* ── URL sharing ── */
    buildShareUrl: function(config) {
      var params = [];
      var fields = ['range', 'size', 'fabric', 'cushion', 'cover', 'piping',
                     'cushionFill', 'legStyle', 'legFinish', 'frame'];
      fields.forEach(function(f) {
        if (config[f]) params.push(f + '=' + encodeURIComponent(config[f]));
      });
      /* Prefer the product page the build was configured on (stored as
         pageUrl), so opening a saved build from anywhere returns to the right
         sofa. Fall back to the current page for older builds without it. */
      var base = config.pageUrl || (window.location.origin + window.location.pathname);
      return base + '?config=' + btoa(params.join('&'));
    },

    readShareUrl: function() {
      if (typeof window === 'undefined') return null;
      var urlParams = new URLSearchParams(window.location.search);
      var encoded = urlParams.get('config');
      if (!encoded) return null;
      try {
        var decoded = atob(encoded);
        var config = {};
        decoded.split('&').forEach(function(pair) {
          var parts = pair.split('=');
          if (parts.length === 2) config[decodeURIComponent(parts[0])] = decodeURIComponent(parts[1]);
        });
        return config;
      } catch(e) {
        return null;
      }
    },

    /* ── Saved builds display ── */
    renderSavedBuilds: function() {
      var builds = SC.saves.getBuilds();
      if (!builds.length) return '';

      var user = SC.saves.getUser();
      var greeting = user.firstName ? user.firstName + ', you have' : 'You have';

      var cards = builds.map(function(b) {
        var title = (b.rangeName || b.range || 'Sofa');
        /* For a bespoke / COM fabric, "Customer's Own Material" is not an
           accurate label for a saved configuration — show a neutral
           "Custom configuration" instead. */
        var fabricLabel = b.fabricName || b.fabric || '';
        var isCustom = b.fabric === 'bespoke' || /own material|own fabric/i.test(fabricLabel);
        var subtitle = isCustom
          ? [b.size, 'Custom configuration'].filter(Boolean).join(' · ')
          : [b.size, fabricLabel].filter(Boolean).join(' in ');
        var priceHtml = b.price ? '<span class="sc-saved-price">&pound;' + Number(b.price).toLocaleString() + '</span>' : '';
        var imgHtml = b.imageUrl
          ? '<img src="' + b.imageUrl + '" alt="' + title + '" class="sc-saved-img" loading="lazy" />'
          : '<div class="sc-saved-img-placeholder"></div>';

        return '' +
          '<div class="sc-saved-card" data-sc-saved-id="' + b.id + '">' +
            imgHtml +
            '<div class="sc-saved-info">' +
              '<h4 class="sc-saved-title">' + title + '</h4>' +
              '<p class="sc-saved-subtitle">' + subtitle + '</p>' +
              priceHtml +
            '</div>' +
            '<div class="sc-saved-actions">' +
              '<a href="' + SC.saves.buildShareUrl(b) + '" class="sc-saved-load">Open</a>' +
              '<button type="button" class="sc-saved-delete" data-sc-delete-build="' + b.id + '" aria-label="Remove">&times;</button>' +
            '</div>' +
          '</div>';
      }).join('');

      return '' +
        '<div class="sc-saved-builds">' +
          '<div class="sc-saved-header">' +
            '<div class="sc-eyebrow">Your saved builds</div>' +
            '<p class="sc-saved-count">' + greeting + ' ' + builds.length + ' saved ' + (builds.length === 1 ? 'build' : 'builds') + '.</p>' +
          '</div>' +
          '<div class="sc-saved-grid">' + cards + '</div>' +
        '</div>';
    },

    /* Open a saved-builds overlay. Reuses the favourites overlay shell so
       the styling matches. Builds come from the synced store, so this shows
       whatever has been saved on any device for the signed-in email. */
    openBuildsOverlay: function() {
      var existing = document.querySelector('.sc-builds-overlay');
      if (existing) existing.remove();

      var builds = SC.saves.getBuilds();
      var bodyHtml;
      if (!builds.length) {
        bodyHtml = '<div class="sc-fav-empty"><p class="sc-lede">No saved builds yet. Configure a sofa and tap Save to keep it here.</p></div>';
      } else if (window.SC && SC.gallery && SC.gallery.buildCard) {
        /* Use the gallery's card renderer for visual consistency */
        bodyHtml = '<section class="sc-fav-section sc-fav-section-builds">' +
          '<div class="sc-fav-grid">' + builds.map(function(b) { return SC.gallery.buildCard(b); }).join('') + '</div>' +
        '</section>';
      } else {
        bodyHtml = SC.saves.renderSavedBuilds();
      }

      var user = SC.saves.getUser();
      var countText = (user && user.firstName ? user.firstName + ', you have ' : 'You have ') + builds.length + ' saved ' + (builds.length === 1 ? 'build' : 'builds') + '.';

      var overlay = document.createElement('div');
      overlay.className = 'sc-fav-overlay sc-builds-overlay';
      overlay.innerHTML =
        '<div class="sc-fav-panel" role="dialog" aria-modal="true" aria-labelledby="sc-builds-title">' +
          '<header class="sc-fav-head">' +
            '<div>' +
              '<div class="sc-eyebrow">Your saved builds</div>' +
              '<h2 id="sc-builds-title" class="sc-display-2">A few of your <em>favourite things</em>.</h2>' +
              '<p class="sc-fav-count">' + countText + '</p>' +
            '</div>' +
            '<button class="sc-icon-btn sc-fav-close" aria-label="Close" data-sc-builds-close>&times;</button>' +
          '</header>' +
          '<div class="sc-fav-actions">' +
            /* Only with a build in hand. The Studio sends anyone who has
               saved nothing to the gallery instead, so offering it to an
               empty overlay would be a link that bounces on arrival. A
               build also guarantees there is something for the page to be
               about. Primary, because seeing the sofa in your own room is
               a better next step than a PDF of it. */
            (builds.length
              ? '<a class="sc-btn sc-btn-primary" href="' + STUDIO_URL + '">See it in your room</a>'
              : '') +
            '<button class="sc-btn' + (builds.length ? ' sc-btn-secondary' : ' sc-btn-primary') + '" type="button" data-sc-builds-pdf' + (builds.length === 0 ? ' disabled' : '') + '>Download as PDF</button>' +
          '</div>' +
          '<div class="sc-fav-body">' + bodyHtml + '</div>' +
        '</div>';
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(function() { overlay.classList.add('is-open'); });

      function closeOverlay() {
        overlay.classList.remove('is-open');
        document.body.style.overflow = '';
        setTimeout(function() { overlay.remove(); }, 300);
      }

      overlay.addEventListener('click', function(e) {
        if (e.target.closest('[data-sc-builds-close]') ||
            (e.target.classList.contains('sc-fav-overlay') && !e.target.closest('.sc-fav-panel'))) {
          closeOverlay();
          return;
        }
        if (e.target.closest('[data-sc-builds-pdf]')) {
          if (window.SC && SC.gallery && SC.gallery.requestPdf) {
            SC.gallery.requestPdf();
          }
          return;
        }
        var del = e.target.closest('[data-sc-delete-build]');
        if (del) {
          e.preventDefault();
          SC.saves.deleteBuild(del.getAttribute('data-sc-delete-build'));
          /* Re-render in place */
          closeOverlay();
          setTimeout(function() { SC.saves.openBuildsOverlay(); }, 310);
        }
      });

      document.addEventListener('keydown', function onEsc(e) {
        if (e.key === 'Escape' && document.querySelector('.sc-builds-overlay.is-open')) {
          closeOverlay();
          document.removeEventListener('keydown', onEsc);
        }
      });
    }
  };

  /* ── Header integration ── */
  function initHeaderSignIn() {
    /* Update display name if already signed in */
    updateSignInDisplay();
    initHeaderHeart();

    /* Wire all sign-in trigger buttons */
    document.querySelectorAll('[data-sc-signin-trigger]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        if (SC.saves.isSignedIn()) {
          /* Already signed in: show a mini menu or go to saved builds */
          toggleAccountDropdown(btn);
        } else {
          SC.saves.promptSignIn(function() {
            updateSignInDisplay();
            /* Close the mobile drawer if open */
            var drawer = document.getElementById('sc-drawer');
            if (drawer && drawer.classList.contains('is-open')) {
              drawer.classList.remove('is-open');
              drawer.setAttribute('aria-hidden', 'true');
            }
          });
        }
      });
    });
  }

  /* Wire the header heart button on every page.
     On gallery pages sc-gallery.js handles this, so we skip if it's
     already wired. On non-gallery pages, opens the builds overlay
     (or shows an empty-state prompt to visit the gallery). */
  function initHeaderHeart() {
    var hearts = document.querySelectorAll('.sc-icon-btn[aria-label="Wishlist"]');
    hearts.forEach(function(btn) {
      if (btn.hasAttribute('data-sc-heart-wired')) return;
      btn.setAttribute('data-sc-heart-wired', '1');

      btn.addEventListener('click', function(e) {
        e.preventDefault();
        /* If gallery overlay is available, use that */
        if (window.SC && SC.gallery && SC.gallery.openFavourites) {
          SC.gallery.openFavourites();
          return;
        }
        /* Otherwise open builds overlay, or show empty prompt */
        var builds = SC.saves.getBuilds();
        var favCount = 0;
        try { favCount = JSON.parse(localStorage.getItem(userKey('sc_favourites')) || '[]').length; } catch(e) {}
        if (builds.length > 0 || favCount > 0) {
          SC.saves.openBuildsOverlay();
        } else {
          /* Nothing saved — show a prompt */
          var overlay = document.createElement('div');
          overlay.className = 'sc-fav-overlay sc-builds-overlay';
          overlay.innerHTML =
            '<div class="sc-fav-panel" role="dialog" aria-modal="true">' +
              '<header class="sc-fav-head">' +
                '<div>' +
                  '<div class="sc-eyebrow">Your saves</div>' +
                  '<h2 class="sc-display-2">Nothing saved yet</h2>' +
                '</div>' +
                '<button class="sc-icon-btn sc-fav-close" aria-label="Close" data-sc-empty-close>&times;</button>' +
              '</header>' +
              '<div class="sc-fav-body">' +
                '<div class="sc-fav-empty">' +
                  '<p class="sc-lede">Visit the gallery to start saving sofas you love.</p>' +
                  '<a href="/pages/gallery" class="sc-btn sc-btn-primary" style="margin-top:16px;display:inline-block;">Visit the gallery</a>' +
                '</div>' +
              '</div>' +
            '</div>';
          document.body.appendChild(overlay);
          document.body.style.overflow = 'hidden';
          requestAnimationFrame(function(){ overlay.classList.add('is-open'); });
          overlay.addEventListener('click', function(ev) {
            if (ev.target.closest('[data-sc-empty-close]') ||
                (ev.target.classList.contains('sc-fav-overlay') && !ev.target.closest('.sc-fav-panel'))) {
              overlay.classList.remove('is-open');
              document.body.style.overflow = '';
              setTimeout(function(){ overlay.remove(); }, 300);
            }
          });
        }
      });

      /* Badge showing builds + favourites count */
      if (!btn.querySelector('.sc-header-fav-count')) {
        var badge = document.createElement('span');
        badge.className = 'sc-header-fav-count';
        badge.setAttribute('aria-hidden', 'true');
        btn.appendChild(badge);
      }
    });
    syncHeaderHeartBadge();
  }

  function syncHeaderHeartBadge() {
    var builds = SC.saves.getBuilds();
    var favCount = 0;
    try { favCount = JSON.parse(localStorage.getItem(userKey('sc_favourites')) || '[]').length; } catch(e) {}
    var total = builds.length + favCount;
    document.querySelectorAll('.sc-header-fav-count').forEach(function(b) {
      b.textContent = total > 0 ? String(total) : '';
      b.setAttribute('data-count', String(total));
    });
  }

  function updateSignInDisplay() {
    var user = SC.saves.getUser();
    var nameEls = document.querySelectorAll('[data-sc-signin-name]');
    nameEls.forEach(function(el) {
      if (user && user.signedIn && user.firstName) {
        el.textContent = user.firstName;
      } else {
        el.textContent = 'Sign in';
      }
    });
  }

  function toggleAccountDropdown(triggerBtn) {
    var existing = document.querySelector('.sc-account-dropdown');
    if (existing) { existing.remove(); return; }

    var user = SC.saves.getUser();
    var builds = SC.saves.getBuilds();
    /* Build favourites: sofas hearted on a product page live in the favourites
       store (flagged isBuild + .build), not in getBuilds(). The overlay merges
       both when rendering, so the dropdown must count both too. */
    var allFavs = SC.saves.getFavouriteObjects() || [];
    var buildFavs = allFavs.filter(function(f){ return f && f.isBuild && f.build; });
    var buildCount = builds.length + buildFavs.length;
    /* Plain favourites: everything that is NOT a build favourite. */
    var favCount = allFavs.length - buildFavs.length;

    var dropdown = document.createElement('div');
    dropdown.className = 'sc-account-dropdown';
    dropdown.innerHTML =
      '<div class="sc-account-dropdown-inner">' +
        '<div class="sc-account-dropdown-greeting">Hi, ' + (user.firstName || '') + '</div>' +
        (user.email ? '<div class="sc-account-dropdown-email">' + user.email + '</div>' : '') +
        '<div class="sc-account-dropdown-stat"><button type="button" class="sc-account-dropdown-link" data-sc-view-all>' + buildCount + ' saved ' + (buildCount === 1 ? 'build' : 'builds') + ', ' + favCount + ' ' + (favCount === 1 ? 'favourite' : 'favourites') + '</button></div>' +
        '<button type="button" class="sc-account-dropdown-signout" data-sc-signout>Sign out</button>' +
      '</div>';

    triggerBtn.parentNode.style.position = 'relative';
    triggerBtn.parentNode.appendChild(dropdown);

    /* One combined line: opens the favourites overlay, which already
       shows saved builds and favourites together (the old dedicated
       builds overlay read only the pure-builds store and missed builds
       hearted on product pages, hence "no saved builds yet"). */
    var viewAllBtn = dropdown.querySelector('[data-sc-view-all]');
    if (viewAllBtn) {
      viewAllBtn.addEventListener('click', function() {
        dropdown.remove();
        if (SC.gallery && SC.gallery.openFavourites) SC.gallery.openFavourites();
        else if (SC.saves.openBuildsOverlay) SC.saves.openBuildsOverlay();
      });
    }
    /* View favourites (opens the gallery favourites overlay if present) */
    var viewFavsBtn = dropdown.querySelector('[data-sc-view-favs]');
    if (viewFavsBtn) {
      viewFavsBtn.addEventListener('click', function() {
        dropdown.remove();
        if (SC.gallery && SC.gallery.openFavourites) SC.gallery.openFavourites();
      });
    }

    /* Sign out handler */
    dropdown.querySelector('[data-sc-signout]').addEventListener('click', function() {
      SC.saves.signOut();
      updateSignInDisplay();
      dropdown.remove();
    });

    /* Close on outside click */
    setTimeout(function() {
      document.addEventListener('click', function closeDropdown(e) {
        if (!dropdown.contains(e.target) && e.target !== triggerBtn) {
          dropdown.remove();
          document.removeEventListener('click', closeDropdown);
        }
      });
    }, 10);
  }

  /* Init on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaderSignIn);
  } else {
    initHeaderSignIn();
  }

})();
